import axios from "axios"
import { resolveDeviceId } from "../Player/deviceId.js"

const HEARTBEAT_INTERVAL_SECONDS = 15

/**
 * Video engagement beacon — sibling of useAudioWorker.js. Sends to the
 * Player's `/embed/{podcast}/listen-video` endpoint, which forwards to the
 * Statistics microservice's pro_video_engagement index.
 *
 * Send cadence (per analytics.md A8):
 * - Events (play/pause/back/forward/mute/unmute/scroll/finalized/quality_switch/
 *   fullscreen_enter/fullscreen_exit/buffering_start/buffering_end) → immediate.
 * - Heartbeat (empty actions, fired on every `timeupdate` from the page) →
 *   throttled to once every HEARTBEAT_INTERVAL_SECONDS, matching audio.
 *
 * Interval semantics: an event is a point (previous_second === second); a
 * heartbeat covers the interval since the previous send (previous_second is
 * the second of that send), so consecutive heartbeats never overlap.
 *
 * `viewSession` is the page's view session id (see viewSession.js), sent as
 * `vs`; the server uses it as vid_session, the same id the HLS segment
 * redirects put on the CDN URLs.
 */
export function useVideoAnalytics({ podcastHash, episodeHash, duration, logUrl, preview = false, viewSession = null, post = (url, payload) => axios.post(url, payload) }) {
    const deviceId = resolveDeviceId()
    let previousSecond = 0
    let lastSendSecond = -HEARTBEAT_INTERVAL_SECONDS

    function send(actions, currentSecond, eventState = {}) {
        // Preview / internal sessions deliberately don't pollute the index —
        // matches the audio player's behavior of swallowing tracking in preview.
        if (preview) {
            return
        }

        const second = Math.ceil(currentSecond ?? 0) || 0
        const isEvent = Object.keys(actions).length > 0

        // Throttle heartbeats. Events always fire and also reset the heartbeat
        // clock so we don't emit a heartbeat right after (e.g.) a play event.
        if (!isEvent && second - lastSendSecond < HEARTBEAT_INTERVAL_SECONDS) {
            return
        }

        const previous = isEvent ? second : previousSecond
        previousSecond = second
        lastSendSecond = second

        const payload = {
            podcast: podcastHash,
            episode: episodeHash,
            duration,
            second,
            previous_second: previous,
            actions,
            device: deviceId,
            content_type: "video",
        }

        if (viewSession) {
            payload.vs = viewSession
        }

        // Video-specific per-event state. Server-side validation makes these
        // optional, so undefined keys just won't be present in the doc.
        if (typeof eventState.qualityHeight === "number") {
            payload.quality_height = eventState.qualityHeight
        }
        if (typeof eventState.bufferingMs === "number") {
            payload.buffering_ms = eventState.bufferingMs
        }
        if (typeof eventState.fullscreen === "boolean") {
            payload.fullscreen = eventState.fullscreen
        }
        if (eventState.viewportPixels && typeof eventState.viewportPixels.width === "number") {
            payload.viewport_pixels = eventState.viewportPixels
        }
        if (eventState.ctaId) {
            payload.cta_id = String(eventState.ctaId)
        }

        Promise.resolve(post(logUrl, payload)).catch((err) => {
            // Beacon failures should never break playback. Surface in dev only.
            if (typeof console !== "undefined" && console.warn) {
                console.warn("[VideoStream] analytics beacon failed", err?.message ?? err)
            }
        })
    }

    return { send }
}
